import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building, Clock, Users, Square, Star, MessageCircle } from 'lucide-react';
import { mockProjects, mockServices, mockTestimonials, companyStats } from '../mock';

const Homepage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [statsAnimated, setStatsAnimated] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('NIVASAM');
  const videoRef = React.useRef(null);

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % mockTestimonials.length);
    }, 5000);
    return () => clearInterval(testimonialInterval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.getElementById('stats-section');
      if (statsSection && !statsAnimated) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setStatsAnimated(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [statsAnimated]);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919884611368?text=Hello, I am interested in NPSH Developers projects', '_blank');
  };

  const getIcon = (iconName) => {
    const icons = {
      Building: Building,
      Clock: Clock,
      Users: Users,
      Square: Square
    };
    const IconComponent = icons[iconName] || Building;
    return <IconComponent className="h-8 w-8" style={{ color: "#ca8a04" }} />;
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-transparent min-h-screen flex items-center py-20 lg:py-28 z-10 overflow-hidden">
        {/* Background Video only for hero section */}
        {/* Desktop Video */}
        <video
          ref={videoRef}
          src="pics/intro background pc.mp4"
          className="hidden sm:block absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          muted={isVideoMuted}
          playsInline
          style={{
            opacity: 0.75,
            pointerEvents: 'auto',
            width: '100vw',
            height: '100vh',
            minWidth: 0,
            minHeight: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            left: 0,
            top: 0
          }}
          onClick={() => setIsVideoModalOpen(true)}
        />
        {/* Mobile Video */}
        <video
          src="pics/intro background mob.mp4"
          className="block sm:hidden absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          muted
          playsInline
          style={{
            opacity: 0.75,
            pointerEvents: 'auto',
            width: '100vw',
            height: '100vh',
            minWidth: 0,
            minHeight: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            left: 0,
            top: 0
          }}
        />
        {/* Centered content above logo - moved to top */}
        <div className="absolute top-0 left-1/2 w-full max-w-3xl -translate-x-1/2 flex flex-col items-center pt-2 z-20 px-2 sm:px-0">
          <h1 className="text-4xl lg:text-6xl font-bold text-center" style={{ color: '#ca8a04' }}>
            NPSH <span className="text-gray-800">DEVELOPERS</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed text-center mt-4 mb-8 px-2">
            Building Chennai’s future since 2017 with thoughtfully designed homes and architectural excellence — delivering comfort, quality, and elegance in every square foot across the city’s thriving neighborhoods.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleWhatsAppClick}
              className="inline-flex items-center justify-center px-8 py-4"
              style={{ background: "#ca8a04", color: "#fff", fontSize: "1.125rem", borderRadius: "0.5rem" }}
            >
              <MessageCircle className="mr-2 h-6 w-6" />
              Enquire on WhatsApp
            </button>
            <Link
              to="/projects"
              className="inline-flex items-center justify-center px-8 py-4 border-2 rounded-lg text-lg font-semibold transition-all duration-300"
              style={{ borderColor: "#ca8a04", color: "#ca8a04" }}
              onMouseOver={e => (e.currentTarget.style.background = "#ca8a04", e.currentTarget.style.color="#fff")}
              onMouseOut={e => (e.currentTarget.style.background = "transparent", e.currentTarget.style.color="#ca8a04")}
            >
              View Projects
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
          </div>
        </div>
        {/* ...existing logo/video grid below... */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          
        </div>
      </section>

      {/* Company Stats */}
      <section id="stats-section" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {companyStats.map((stat, index) => (
              <div
                key={index}
                className={`text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 transform transition-all duration-700 ${
                  statsAnimated ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="mb-4 flex justify-center">
                  {getIcon(stat.icon)}
                </div>
                <p className="text-3xl font-bold text-gray-800 mb-2">{stat.value}</p>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold" style={{ color: "#ca8a04" }}>Featured Projects</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our thoughtfully crafted homes and commercial spaces — where innovative design meets unmatched quality, right in the heart of Chennai’s most vibrant neighborhoods.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {mockProjects.slice(0, 3).map((project) => (
              <div
                key={project.id}
                className="group bg-white rounded-2xl shadow-lg border-2 border-gray-100 hover:border-yellow-600 transition-all duration-300 overflow-hidden transform hover:scale-105"
              >
                <div className="aspect-video bg-gray-200 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium bg-yellow-50 px-3 py-1 rounded-full"
                      style={{ color: "#ca8a04" }}>
                      {project.type}
                    </span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      project.status === 'Available' ? 'bg-green-100 text-green-800' :
                      project.status === 'Under Construction' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-yellow-600 transition-colors duration-200">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{project.location}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-800">{project.price}</span>
                    <Link
                      to="/projects"
                      state={{ showProject: project.title }}
                      className="text-yellow-600 hover:text-yellow-700 font-medium flex items-center group"
                    >
                      View Details
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Metallic gold container for video and description below project cards */}
          <div className="mb-8 flex justify-center">
            <div className="border-4 border-[#ca8a04] rounded-2xl shadow-lg p-4 sm:p-8 lg:p-12 flex flex-col items-center w-full max-w-5xl bg-white">
              {/* Project Title */}
              <h3 className="text-3xl font-bold mb-4" style={{ color: '#ca8a04', letterSpacing: 1 }}>{`Exclusive Launch: ${projectName}`}</h3>
              {/* Place your video here */}
              <div className="w-full aspect-[16/7] bg-black rounded-xl overflow-hidden mb-4 flex items-center justify-center" style={{ minHeight: '180px', maxHeight: '420px' }}>
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/DeRDtYKnxyQ"
                  title="NIVASAM Project Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{ borderRadius: '0.75rem', background: 'black' }}
                ></iframe>
              </div>
              {/* Small description */}
              <p className="text-center text-lg font-semibold" style={{ color: '#ca8a04' }}>An exclusive gated community of 16 premium 2 & 3 BHK residences designed to deliver elegant urban living with spacious layouts, high-end fittings, and refined finishes. Located in the heart of Arumbakkam, it offers quick access to top schools, hospitals, theatres, and temples — blending comfort, security, and convenience.</p>
            </div>
          </div>
          <div className="text-center">
            <Link
              to="/projects"
              className="inline-flex items-center px-8 py-3"
              style={{ background: "#ca8a04", color: "#fff", borderRadius: "0.5rem", fontWeight: 600 }}
              onMouseOver={e => (e.currentTarget.style.background = "#a97800")}
              onMouseOut={e => (e.currentTarget.style.background = "#ca8a04")}
            >
              View All Projects
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "#ca8a04" }}>Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive construction and development services from planning to handover.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockServices.map((service) => (
              <div
                key={service.id}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-yellow-600 transition-all duration-300 text-center group hover:shadow-lg"
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-colors duration-300"
                  style={{ background: "#F7E6B0" }}>
                  <Building className="h-8 w-8" style={{ color: "#ca8a04" }} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-yellow-600 transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "#ca8a04" }}>What Our Clients Say</h2>
            <p className="text-xl text-gray-600">
              Trusted by hundreds of happy families and businesses across Chennai.
            </p>
          </div>
          <div className="relative">
            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-yellow-100">
              <div className="flex items-center mb-6">
                <img
                  src={mockTestimonials[currentTestimonial].image}
                  alt={mockTestimonials[currentTestimonial].name}
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="text-xl font-bold text-gray-800">{mockTestimonials[currentTestimonial].name}</h4>
                  <p className="font-medium" style={{ color: "#ca8a04" }}>{mockTestimonials[currentTestimonial].project}</p>
                </div>
                <div className="ml-auto flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>
              <blockquote className="text-lg text-gray-600 italic leading-relaxed">
                "{mockTestimonials[currentTestimonial].comment}"
              </blockquote>
            </div>
            {/* Testimonial Indicators */}
            <div className="flex justify-center mt-8 space-x-2">
              {mockTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                    currentTestimonial === index ? 'bg-yellow-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
